import React, { useEffect } from 'react';
import moment from 'moment';
import CurrencyFormat from 'react-currency-format';
import cn from '@/utils/cn';
import { CloseIcon } from '@/components/Icons';
import Button from '@/components/Button';
import { batchPaymentsPage, scheduledTransferModal } from '@/utils/translations';
import { DATE_FORMAT_FULL } from '@/utils/dateFormats';
import { ScheduledBankPaymentGroupStateEnum } from '@/features/payments/types/BatchPayment';
import { TPaymentListItem } from '../types/ManagePayments';
import { STATUS_STYLE, isPaymentCreator } from '../data/paymentStatus';
import { PaymentActionVariantEnum } from '../data/paymentActions';
import { UnableToDeleteReasonEnum } from '../data/unableToDeleteReason';
import DetailRow from './DetailRow';
import DeleteConfirmModal from './DeleteConfirmModal';
import UnableToDeleteModal from './UnableToDeleteModal';
import PaymentOutcomeResult from './PaymentOutcomeResult';
import { DESTRUCTIVE_IDLE_CLASS } from './actionButtonClasses';
import useAuth from '@/hooks/useAuth';
import { useScheduledPaymentAction, TPaymentUpdate } from '../hooks/useScheduledPaymentAction';

// A scheduled transfer never carries a multiSignActionId (see TRsScheduledWalletTransfer), so its
// modal only ever has two things to show: an upcoming (APPROVED/"Scheduled") transfer with just a
// Delete action, or a FAILED one with a Delete + Transfer now pair - there's no approve/reject,
// no "Created by"/statement detail, and no multi-sign approval banner to account for, unlike
// ScheduledPaymentModal, which this is otherwise modelled on.
const TITLE_ID = 'scheduled-transfer-title';

interface ScheduledTransferModalProps {
  isOpen: boolean;
  onClose: () => void;
  payment: TPaymentListItem | null;
  /** Lets the table remove this row after a successful delete or retry, without a refetch. */
  onPaymentDelete?: (id: string) => void;
  /** Passed through for symmetry with ScheduledPaymentModal's props - unused (see module comment). */
  onPaymentUpdate?: (update: TPaymentUpdate) => void;
}

const ScheduledTransferModal: React.FC<ScheduledTransferModalProps> = ({
  isOpen,
  onClose,
  payment,
  onPaymentDelete,
  onPaymentUpdate
}) => {
  const auth = useAuth();

  const {
    variant: actionVariant,
    hasDeleted,
    openDeleteDialog,
    isDeleteDialogOpen,
    closeDeleteDialog,
    confirmDelete,
    isDeleting,
    deleteError,
    unableToDeleteReason,
    dismissUnableToDelete,
    retry,
    isRetrying,
    retryError,
    hasRetried,
    hasImmediateFailure
  } = useScheduledPaymentAction(payment, { onPaymentUpdate, onPaymentDelete });

  // No dedicated "transfer deleted" screen exists - the row disappearing from the table already
  // communicates success (same reasoning as ScheduledPaymentModal's own delete). hasRetried is
  // NOT included here - unlike delete, a successful Transfer now gets its own transferComplete
  // result screen below (see useScheduledPaymentAction's retry()), so it must stay open until the
  // user closes it.
  useEffect(() => {
    if (hasDeleted) onClose();
  }, [hasDeleted, onClose]);

  if (!isOpen || !payment) return null;

  const isFailed = payment.state === ScheduledBankPaymentGroupStateEnum.FAILED;
  const statusLabel = isFailed
    ? scheduledTransferModal.failedStatusLabel
    : batchPaymentsPage.status.scheduled;

  // Same creator-only restriction as ScheduledPaymentModal's Delete (see isPaymentCreator) - kept
  // for consistency even though a scheduled transfer never has co-approvers to defer to.
  const isCreator = isPaymentCreator(payment.createdByCustomerId, auth?.customer?.id);
  const showDeleteAction =
    (actionVariant === PaymentActionVariantEnum.DELETE ||
      actionVariant === PaymentActionVariantEnum.RETRY_AND_DELETE) &&
    isCreator;

  return (
    <div
      className="fixed inset-0 z-99 flex justify-center overflow-y-auto bg-aux-lightgray"
      role="dialog"
      aria-modal="true"
      aria-labelledby={TITLE_ID}>
      <button
        type="button"
        onClick={onClose}
        aria-label={scheduledTransferModal.closeLabel}
        className="absolute right-6 top-6 flex h-[38px] w-[38px] items-center justify-center rounded-full border border-aux-gray bg-white hover:bg-emerge-base-3">
        <CloseIcon onClick={() => {}} size="sm" />
      </button>

      {/* Anchored near the top, matching Pay/Batch Payment/Transfer Money's own modal layout
          (see e.g. src/pages/Home/TransferMoney/TransferMoney.tsx) - not vertically centered. */}
      <div className="w-107.5 max-w-full pt-16">
        {hasImmediateFailure ? (
          // Transfer now came back with a confirmed insufficient-funds error (see
          // useScheduledPaymentAction's retry()) - takes over from the normal detail view below,
          // same reasoning as ScheduledPaymentModal's own hasImmediateFailure branch.
          <PaymentOutcomeResult
            variant="transferFailed"
            onClose={onClose}
            titleId={TITLE_ID}
            accountName={payment.payFromAccount?.name}
          />
        ) : hasRetried ? (
          // Transfer now succeeded (a bare 200 with no body - see retryScheduledWalletTransfer) -
          // a retry always runs synchronously, so success here always means "complete".
          <PaymentOutcomeResult
            variant="transferComplete"
            onClose={onClose}
            titleId={TITLE_ID}
            accountName={payment.payFromAccount?.name}
          />
        ) : (
          <>
            <div className="rounded-t-2xl border border-aux-gray bg-emerge-base-3 px-4 py-[18px]">
              <h4 id={TITLE_ID} className="text-center text-2xl font-medium text-emerge-navy">
                {scheduledTransferModal.title}
              </h4>
            </div>

            <div className="flex flex-col gap-y-4 rounded-b-2xl border-x border-b border-aux-gray bg-white p-6">
              <CurrencyFormat
                value={Number(payment.totalAmount).toFixed(2)}
                displayType="text"
                thousandSeparator
                prefix="$"
                decimalScale={2}
                className="text-center text-5xl font-medium text-emerge-navy"
              />
              <span
                className={cn('text-center text-[13px] font-medium', STATUS_STYLE[payment.state])}>
                {statusLabel}
              </span>

              <div className="flex flex-col gap-y-0.5">
                <DetailRow
                  label={scheduledTransferModal.fromLabel}
                  value={payment.payFromAccount?.name ?? scheduledTransferModal.emptyValue}
                  filled
                />
                <DetailRow
                  label={scheduledTransferModal.toLabel}
                  value={payment.name || scheduledTransferModal.emptyValue}
                />
                <DetailRow
                  label={scheduledTransferModal.noteLabel}
                  value={payment.note || scheduledTransferModal.emptyValue}
                  filled
                />
              </div>

              {isFailed ? (
                <>
                  <div className="flex justify-center">
                    <span className="rounded-full bg-emerge-base-3 px-4 py-1 text-2xs font-medium text-emerge-navy">
                      {scheduledTransferModal.asapPillLabel}
                    </span>
                  </div>

                  <div className="flex flex-col gap-y-1 rounded-[10px] bg-aux-lightred p-4">
                    <span className="text-xs font-medium text-error-darkred">
                      {scheduledTransferModal.failedBannerTitle}
                    </span>
                    <span className="text-2xs font-medium text-error-darkred">
                      {scheduledTransferModal.failedBannerBody}
                    </span>
                  </div>
                </>
              ) : (
                <div className="flex justify-center pt-2">
                  <span className="rounded-full bg-emerge-base-3 px-4 py-1 text-2xs font-medium text-emerge-navy">
                    {moment(payment.scheduledDate).format(DATE_FORMAT_FULL)}
                  </span>
                </div>
              )}

              {showDeleteAction && actionVariant === PaymentActionVariantEnum.DELETE && (
                <Button
                  variant="primary_inverted"
                  size="pill"
                  onClick={openDeleteDialog}
                  disabled={isDeleting}
                  className={cn(DESTRUCTIVE_IDLE_CLASS, 'mt-6 w-full')}>
                  {isDeleting
                    ? scheduledTransferModal.deletingButton
                    : scheduledTransferModal.deleteButton}
                </Button>
              )}

              {actionVariant === PaymentActionVariantEnum.RETRY_AND_DELETE && (
                <>
                  <div className="mt-6 flex items-center gap-x-[19px]">
                    {/* Same creator restriction as showDeleteAction above - retry (sending the
                        transfer now) stays available to any admin who can reach this variant, only
                        delete is creator-only. Transfer now takes the full row when delete is hidden. */}
                    {isCreator && (
                      <Button
                        variant="primary_inverted"
                        size="pill"
                        onClick={openDeleteDialog}
                        disabled={isDeleting || isRetrying}
                        className={cn(DESTRUCTIVE_IDLE_CLASS, 'w-auto flex-1 whitespace-nowrap')}>
                        {isDeleting
                          ? scheduledTransferModal.deletingButton
                          : scheduledTransferModal.deleteButton}
                      </Button>
                    )}
                    <Button
                      size="pill"
                      onClick={retry}
                      disabled={isDeleting || isRetrying}
                      className={isCreator ? 'w-[185px]' : 'w-full'}>
                      {isRetrying
                        ? scheduledTransferModal.transferringButton
                        : scheduledTransferModal.transferNowButton}
                    </Button>
                  </div>
                  {retryError && (
                    <p role="alert" className="text-center text-xs font-medium text-error-darkred">
                      {retryError}
                    </p>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>

      <DeleteConfirmModal
        isOpen={isDeleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={confirmDelete}
        isSubmitting={isDeleting}
        error={deleteError}
        headerText={scheduledTransferModal.deleteConfirmTitle}
        bodyText={scheduledTransferModal.deleteConfirmBody}
      />

      <UnableToDeleteModal
        isOpen={!!unableToDeleteReason}
        onClose={dismissUnableToDelete}
        headerText={scheduledTransferModal.deleteBlockedTitle}
        bodyText={
          unableToDeleteReason === UnableToDeleteReasonEnum.WITHIN_BLOCK_WINDOW
            ? scheduledTransferModal.deleteBlockedBody
            : scheduledTransferModal.deleteConflictBody
        }
        buttonText={scheduledTransferModal.closeButton}
      />
    </div>
  );
};

export default ScheduledTransferModal;
